import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "@/store/store";
import type { AuthRole } from "@/domain/auth/auth.api.types";
import type { AppUser, AuthenticatedUser, UserEnrichment } from "@/domain/auth/authenticated-user";
import type { AuthSessionState, AuthStatus } from "@/domain/auth/auth.state";
import {
  projectCheckoutAddresses,
  resolveCheckoutAddress,
} from "@/domain/address/checkout-address-projection";

export const selectAuthState = (state: RootState) => state.auth;

export const selectAuthStatus = createSelector(selectAuthState, (auth): AuthStatus => auth.status);

export const selectAuthError = createSelector(selectAuthState, (auth) => auth.error);

export const selectIsAuthenticated = createSelector(
  selectAuthStatus,
  (status) => status === "authenticated"
);

export const selectIsAuthLoading = createSelector(selectAuthStatus, (status) =>
  ["idle", "authenticating", "loadingProfile", "refreshing"].includes(status)
);

export const selectAppUser = createSelector(selectAuthState, (auth): AppUser | null => auth.user);

export const selectIdentity = createSelector(
  selectAppUser,
  (user): AuthenticatedUser | null => user?.identity ?? null
);

export const selectUserId = createSelector(selectIdentity, (identity) => identity?.userId ?? null);

export const selectFirstName = createSelector(
  selectIdentity,
  (identity) => identity?.firstName ?? ""
);

export const selectLastName = createSelector(selectIdentity, (identity) => identity?.lastName ?? "");

export const selectDisplayName = createSelector(
  selectIdentity,
  (identity) => identity?.displayName ?? ""
);

export const selectEmail = createSelector(selectIdentity, (identity) => identity?.email ?? "");

export const selectRoles = createSelector(selectIdentity, (identity): AuthRole[] => identity?.roles ?? []);

export const selectHasRole = (role: AuthRole) =>
  createSelector(selectRoles, (roles) => roles.includes(role));

export const selectEnrichment = createSelector(
  selectAppUser,
  (user): UserEnrichment | null => user?.enrichment ?? null
);

/** Raw server address list — never carries a selection. */
export const selectRawAddresses = createSelector(
  selectEnrichment,
  (enrichment) => enrichment?.addresses ?? []
);

/**
 * Checkout selection id. Optional chaining is deliberate: test fixtures build partial
 * stores without the `checkoutAddress` reducer.
 */
export const selectSelectedCheckoutAddressId = (state: RootState): string | null =>
  state.checkoutAddress?.selectedAddressId ?? null;

/**
 * Compatibility projection: the raw list with `isDefault` derived from the checkout
 * selection (any incoming `isDefault` is overwritten). Existing consumers keep reading
 * `addresses.find(a => a.isDefault)`.
 */
export const selectAddresses = createSelector(
  [selectRawAddresses, selectSelectedCheckoutAddressId],
  (addresses, selectedAddressId) => projectCheckoutAddresses(addresses, selectedAddressId)
);

export const selectDefaultAddress = createSelector(
  [selectRawAddresses, selectSelectedCheckoutAddressId],
  (addresses, selectedAddressId) => resolveCheckoutAddress(addresses, selectedAddressId)
);

export const selectAuthSession = createSelector(
  selectAuthState,
  (auth): AuthSessionState | null => auth.session
);

export const selectRequiresRestaurantSelection = createSelector(
  selectAuthSession,
  (session) => session?.requiresRestaurantSelection ?? false
);

export const selectProfileFetchedAt = createSelector(
  selectAuthState,
  (auth) => auth.profileFetchedAt
);
